import type {State} from 'src/reducers';
import {createSelector} from 'reselect';
import {CARD_TYPES} from 'src/components/messaging/broadcast-suppression/constants';
import type {BroadcastSuppressionPhoneDetails} from 'src/components/messaging/broadcast-suppression/types';
import type {
  GenericOptionsType,
  fileCsvUploadEntries,
  csvMapping,
  ContactListDetails,
  FileDetails,
  HVBroadcastSuppressionTimeFrame,
  HVBData,
  RenameModalPopupDetails,
  ScheduledOrNewHVBroadcast,
  Segment,
  AutoReply,
  PerformanceDetailsResponse,
} from 'src/types/hv-broadcasts';
import {formatPhone} from 'src/utils/phone';
import {
  selectCurrentPhoneCountry,
  selectPhoneNumberSetFromId,
} from 'src/selectors/phone-number-sets';


export const getHVBroadcastRestoredPhoneNumbers = (state: State): string[] => {
  return state.hvBroadcasts.hvBroadcastRestoredPhoneNumbers;
};

export const getSuppressedNumbersList = (state: State): string[] => {
  return Object.keys(state.hvBroadcasts.hvBroadcastSuppression);
};

export const getSuppressedNumbersDetails = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  // @ts-expect-error - TS2339 - Property 'hvBroadcastSuppression' does not exist on type 'HvBroadcastsState'.
  return Object.values(state.hvBroadcasts.hvBroadcastSuppression);
};
export const getAllHVRecentlyMessaged = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const restoredPhoneNumbers = getHVBroadcastRestoredPhoneNumbers(state);
  const suppressedNumbers: BroadcastSuppressionPhoneDetails[] =
    getSuppressedNumbersDetails(state);
  return suppressedNumbers.filter(
    (phone) =>
      phone.broadcastSuppression &&
      !restoredPhoneNumbers.includes(phone.phoneNumber),
  );
};

export const getAllHVLandlineNumbers = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const restoredPhoneNumbers = getHVBroadcastRestoredPhoneNumbers(state);
  const suppressedNumbers: BroadcastSuppressionPhoneDetails[] =
    getSuppressedNumbersDetails(state);
  const landlineNumbers = suppressedNumbers.filter(
    (phone) =>
      !restoredPhoneNumbers.includes(phone.phoneNumber) &&
      phone.type === 'landline',
  );
  return landlineNumbers;
};

export const getAllHVInvalidNumbers = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const suppressedNumbers: BroadcastSuppressionPhoneDetails[] =
    getSuppressedNumbersDetails(state);
  const invalidNumbers = suppressedNumbers.filter(
    (phone) => phone.type === 'invalid',
  );
  return invalidNumbers;
};

export const getAllHVOptOutNumbers = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const suppressedNumbers: BroadcastSuppressionPhoneDetails[] =
    getSuppressedNumbersDetails(state);
  const optOutNumbers = suppressedNumbers.filter((phone) => phone.optedOut);
  return optOutNumbers;
};

export const getTypesOfHVBroadcastWarnings: (state: State) => string[] =
  createSelector(
    getAllHVRecentlyMessaged,
    getAllHVLandlineNumbers,
    getAllHVInvalidNumbers,
    getAllHVOptOutNumbers,
    (
      recentlyMessaged,
      landlineNumbers,
      invalidNumbers,
      optOutNumbers,
    ): string[] => {
      const allWarnings: Array<string> = [];
      if (recentlyMessaged.length > 0) {
        allWarnings.push(CARD_TYPES.RECENTLY_MESSAGED);
      }
      if (landlineNumbers.length > 0) {
        allWarnings.push(CARD_TYPES.LANDLINE);
      }
      if (invalidNumbers.length > 0) {
        allWarnings.push(CARD_TYPES.INVALID);
      }
      if (optOutNumbers.length > 0) {
        allWarnings.push(CARD_TYPES.OPTOUT);
      }
      return allWarnings;
    },
  );

export const getHVBroadcastSuppressionCount: (state: State) => number =
  createSelector(
    getAllHVRecentlyMessaged,
    getAllHVLandlineNumbers,
    getAllHVInvalidNumbers,
    getAllHVOptOutNumbers,
    (
      recentlyMessaged,
      landlineNumbers,
      invalidNumbers,
      optOutNumbers,
    ): number => {
      return (
        recentlyMessaged.length +
        landlineNumbers.length +
        invalidNumbers.length +
        optOutNumbers.length
      );
    },
  );

export const getIgnoredPhoneNumbersList: (state: State) => string[] =
  createSelector(
    getHVBroadcastRestoredPhoneNumbers,
    getSuppressedNumbersList,
    (restoredPhoneNumbers, suppressedNumbers): string[] => {
      return suppressedNumbers.filter(
        (suppressedNumber) => !restoredPhoneNumbers.includes(suppressedNumber),
      );
    },
  );
export const selectFileCsvUploadHeaders = (state: State): string[] =>
  state.hvBroadcasts.fileCsvUploadHeaders;

export const selectFileCsvUploadEntries = (
  state: State,
): fileCsvUploadEntries[] => state.hvBroadcasts.fileCsvUploadEntries;

export const selectCsvMappingForFileUpload = (state: State): csvMapping => {
  const mappedEntries = state.hvBroadcasts.fileCsvUploadEntries;
  const mapping: Record<string, any> = {};
  mappedEntries.forEach((entry) => {
    const {mappedTo, id} = entry;
    if (mappedTo && id) {
      mapping[mappedTo] = id;
    }
  });
  return mapping;
};

export const selectFileCsvUpload = (state: State): File | null =>
  state.hvBroadcasts.fileCsvUpload;

export const selectInboxesAsOptions = (state: State): GenericOptionsType[] => {
  const countryCode = selectCurrentPhoneCountry(state);
  const {phoneNumberSets} = state.hvBroadcasts.filters;
  // remove this condition when whatsapp is supported in HVB
  const smsInboxes = phoneNumberSets.filter(
    (phoneNumberSet) => phoneNumberSet.sms?.id,
  );
  const inboxValuesToOptions = smsInboxes.map((smsInbox) => {
    const formattedPhone = formatPhone(smsInbox?.number || '', countryCode);
    const label = smsInbox.name || formattedPhone;
    // @ts-expect-error - TS2339 - Property 'sms' does not exist on type 'PhoneNumberSet'.
    return {key: smsInbox.sms.id, label};
  });
  // add all inboxes
  // @ts-expect-error - TS2339 - Property 'sms' does not exist on type 'PhoneNumberSet'.
  return [{key: 'All_inbox', label: 'All inbox'}, ...inboxValuesToOptions];
};

export const selectContactListsAsOptions = (
  state: State,
): GenericOptionsType[] => {
  return state.hvBroadcasts.contactLists.map((list) => {
    return {
      key: list.id,
      label: list.title,
      count: list.contactCounts,
    };
  });
};

export const selectAgentsRecentContactListAsOptions = (
  state: State,
): GenericOptionsType[] => {
  return state.hvBroadcasts.agentsRecentContactLists.map((list) => {
    return {
      key: list.id,
      label: list.title,
      count: list.contactCounts,
    };
  });
};

export const selectContactLists = (state: State): ContactListDetails[] => {
  return state.hvBroadcasts.contactLists.map((list) => {
    return {
      id: list.id,
      name: list.title,
      count: list.contactCounts,
    };
  });
};

export const selectUploadedFiles = (state: State): FileDetails[] => {
  return state.hvBroadcasts.uploadedFileDetails;
};

export const selectBroadcastSuppressionTimeFrame = (
  state: State,
): HVBroadcastSuppressionTimeFrame => {
  return state.hvBroadcasts.broadcastSuppressionTimeFrame;
};

export const selectIsContactListLimitReached = (state: State): boolean => {
  return state.hvBroadcasts.contactListLimitReached;
};

export const selectCreatedContactListId = (state: State): boolean => {
  // @ts-expect-error - TS2339 - Property 'listCreatedId' does not exist on type 'HvBroadcastsState'.
  return state.hvBroadcasts.listCreatedId;
};

export const selectAllBroadcasts = (state: State): HVBData[] => {
  return Object.values(state.hvBroadcasts.hvBroadcasts.broadcasts);
};

export const selectHVBroadcastsCurrentPage = (state: State): number => {
  return state.hvBroadcasts.hvBroadcasts.page;
};

export const selectHVBroadcastsTotalPages = (state: State): number => {
  return state.hvBroadcasts.hvBroadcasts.totalPages;
};

export const selectScheduledBroadcastData = (state: State): HVBData => {
  // @ts-expect-error - TS2339 - Property 'currentBroadcastData' does not exist on type 'HvBroadcastsState'.
  return state.hvBroadcasts.currentBroadcastData;
};

export const selectPhoneNumberSetId = (state: State): string => {
  return state.hvBroadcasts.hvBroadcastMessage.phoneNumberSetId;
};

export const selectRecipientSensePhone = (state: State): string => {
  return state.hvBroadcasts.hvBroadcastPerformance.sensePhone;
};

export const selectInboxPhoneFromInboxId = (
  state: State,
  id: string,
): string => {
  const {phoneNumberSets} = state.hvBroadcasts.filters;
  // remove this condition when whatsapp is supported in HVB
  const selectedInbox = phoneNumberSets.find(
    (phoneNumberSet) => phoneNumberSet.sms?.id === id,
  );
  // @ts-expect-error - TS2339 - Property 'phone' does not exist on type 'MultiInboxDetails'.
  return selectedInbox.phone;
};

export const selectHVBSelectedChannel = (state: State): string => {
  return state.hvBroadcasts.selectedChannel;
};

export const selectSchedulerTemplateId = (state: State): string => {
  return state.hvBroadcasts.schedulerTemplateId;
};

export const selectShowRenameModalPopup = (
  state: State,
): RenameModalPopupDetails => {
  return state.hvBroadcasts.showRenameModalPopupDetails;
};

export const selectHVBroadcastPerformance = (
  state: State,
): PerformanceDetailsResponse => {
  return state.hvBroadcasts.hvBroadcastPerformance;
};

export const selectMessageData = (state: State): ScheduledOrNewHVBroadcast => {
  return state.hvBroadcasts.hvBroadcastMessage;
};

export const selectBroadcastType = (state: State): string => {
  return state.hvBroadcasts.selectedBroadcastType;
};

export const selectBroadcastDetails = (
  state: State,
): ScheduledOrNewHVBroadcast => {
  return state.hvBroadcasts.broadcastDetails;
};

export const selectPhoneNumberFromPhoneNumberSetId = (state: State): string => {
  const phoneNumberSetId = state.hvBroadcasts.broadcastDetails.phoneNumberSetId;
  // @ts-expect-error - TS2339 - Property 'phone_numbers' does not exist on type 'PhoneNumberSet'.
  return selectPhoneNumberSetFromId(phoneNumberSetId)(state)?.phone_numbers[0];
};

export const selectIsHVBHighVolumeLine = (state: State): boolean => {
  const phoneNumberSetId = state.hvBroadcasts.broadcastDetails.phoneNumberSetId;
  return (
    selectPhoneNumberSetFromId(phoneNumberSetId)(state)?.is_high_volume || false
  );
};

export const selectSuppressionApiComplete = (state: State): boolean =>
  state.hvBroadcasts.suppressionApiComplete;

export const getCurrentlyAppliedHVBSuppression = (state: State): boolean =>
  // @ts-expect-error - TS2339 - Property 'applySuppression' does not exist on type 'ScheduledOrNewHVBroadcast'.
  state.hvBroadcasts.broadcastDetails.applySuppression;

export const selectBroadcastPerformanceSegments = (state: State): Segment[] => {
  return state.hvBroadcasts.hvBroadcastPerformance.segments || [];
};

export const selectBroadcastDetailsAutoReplies = (
  state: State,
): AutoReply[] => {
  return state.hvBroadcasts.hvBroadcastMessage.autoReplies || [];
};

export const selectBroadcastDripStepNumber = (state: State): number => {
  return state.hvBroadcasts.broadcastDripStepNumber;
};

export const selectSchedulerTemplateBody = (state: State): string => {
  return state.hvBroadcasts.schedulerTemplateBody;
};
